import {
  faAirbnb,
  faBluesky,
  faFacebookF,
  faFacebookMessenger,
  faFlickr,
  faGoogle,
  faInstagram,
  faLine,
  faLinkedinIn,
  faMastodon,
  faPinterestP,
  faSignalMessenger,
  faSnapchatGhost,
  faSpotify,
  faTelegram,
  faThreads,
  faTiktok,
  faViber,
  faVimeoV,
  faWeixin,
  faWhatsapp,
  faXTwitter,
  faYelp,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faGlobe, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition, IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';

/**
 * Tripadvisor is not part of any FontAwesome pack, so its glyph is hand-added here.
 * Path from Simple Icons (CC0), 24x24 viewBox.
 */
export const faTripadvisor: IconDefinition = {
  prefix: 'fab' as IconPrefix,
  iconName: 'tripadvisor' as IconName,
  icon: [24, 24, [], '', 'M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 6 6 0 0 0 6-5.998 5.982 5.982 0 0 0-1.957-4.432L24 6.648h-4.35a13.573 13.573 0 0 0-7.644-2.353zM12 6.255c1.531 0 3.063.303 4.504.903C13.943 8.138 12 10.43 12 13.1c0-2.671-1.942-4.962-4.504-5.942A11.72 11.72 0 0 1 12 6.256zM6.002 9.157a4.059 4.059 0 1 1 0 8.118 4.059 4.059 0 0 1 0-8.118zm11.992.002a4.057 4.057 0 1 1 .003 8.115 4.057 4.057 0 0 1-.003-8.115zm-11.992 1.93a2.128 2.128 0 0 0 0 4.256 2.128 2.128 0 0 0 0-4.256zm11.992 0a2.128 2.128 0 0 0 0 4.256 2.128 2.128 0 0 0 0-4.256z'],
};

/**
 * Trustpilot is not part of any FontAwesome pack, so its glyph is hand-added here.
 * Path from Simple Icons (CC0), 24x24 viewBox.
 */
export const faTrustpilot: IconDefinition = {
  prefix: 'fab' as IconPrefix,
  iconName: 'trustpilot' as IconName,
  icon: [24, 24, [], '', 'M17.227 16.67l2.19 6.742-7.413-5.388 5.223-1.354zM24 9.31h-9.165L12.005.589l-2.84 8.723L0 9.3l7.422 5.397-2.84 8.714 7.422-5.388 4.583-3.326L24 9.311z'],
};

// Keys match the `icon` string the API returns (see modules/Users/config/social_platforms.php)
const SOCIAL_ICONS: Record<string, IconDefinition> = {
  facebook: faFacebookF,
  instagram: faInstagram,
  twitter: faXTwitter,
  linkedin: faLinkedinIn,
  tiktok: faTiktok,
  whatsapp: faWhatsapp,
  telegram: faTelegram,
  youtube: faYoutube,
  pinterest: faPinterestP,
  snapchat: faSnapchatGhost,
  messenger: faFacebookMessenger,
  viber: faViber,
  signal: faSignalMessenger,
  line: faLine,
  weixin: faWeixin,
  threads: faThreads,
  bluesky: faBluesky,
  mastodon: faMastodon,
  spotify: faSpotify,
  vimeo: faVimeoV,
  flickr: faFlickr,
  google: faGoogle,
  yelp: faYelp,
  airbnb: faAirbnb,
  tripadvisor: faTripadvisor,
  trustpilot: faTrustpilot,
  website: faGlobe,
  email: faEnvelope,
  paperclip: faPaperclip,
};


/**
 * Resolves the `icon` string the API returns (or a platform key) to a FontAwesome definition.
 */
export function socialIconFor(icon: string): IconDefinition {
  return SOCIAL_ICONS[icon] ?? faPaperclip;
}

// Dropdown options for the admin form; keep in sync with SOCIAL_ICONS above.
export const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'viber', label: 'Viber' },
  { value: 'signal', label: 'Signal' },
  { value: 'line', label: 'LINE' },
  { value: 'weixin', label: 'WeChat' },
  { value: 'threads', label: 'Threads' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'mastodon', label: 'Mastodon' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'flickr', label: 'Flickr' },
  { value: 'google', label: 'Google Business Profile' },
  { value: 'yelp', label: 'Yelp' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'tripadvisor', label: 'TripAdvisor' },
  { value: 'trustpilot', label: 'Trustpilot' },
  { value: 'website', label: 'Sito web / Blog' },
  { value: 'email', label: 'Email' },
  { value: 'custom', label: 'Altro' },
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]['value'];

// Accepted brand names per platform: the domain label before the TLD, so facebook.com,
// facebook.it and tripadvisor.co.uk all match. Platforms missing here accept any valid URL
// (website, custom, and mastodon, which is self-hosted on arbitrary domains).
const PLATFORM_BRANDS: Partial<Record<SocialPlatform, string[]>> = {
  facebook: ['facebook', 'fb'],
  instagram: ['instagram', 'instagr'],
  twitter: ['x', 'twitter'],
  linkedin: ['linkedin', 'lnkd'],
  tiktok: ['tiktok'],
  whatsapp: ['whatsapp', 'wa'],
  telegram: ['telegram', 't'],
  youtube: ['youtube', 'youtu'],
  pinterest: ['pinterest', 'pin'],
  snapchat: ['snapchat'],
  messenger: ['messenger', 'm'],
  viber: ['viber'],
  signal: ['signal'],
  line: ['line'],
  weixin: ['wechat', 'qq'],
  threads: ['threads'],
  bluesky: ['bsky'],
  spotify: ['spotify'],
  vimeo: ['vimeo'],
  flickr: ['flickr', 'flic'],
  google: ['google', 'goo', 'g'],
  yelp: ['yelp'],
  airbnb: ['airbnb', 'abnb'],
  tripadvisor: ['tripadvisor'],
  trustpilot: ['trustpilot'],
};

// Second-level label, skipping compound TLDs like co.uk or com.br.
function brandOf(host: string): string {
  const labels = host.split('.');
  const second = labels[labels.length - 2] ?? '';
  return ['co', 'com', 'net', 'org', 'ac', 'gov'].includes(second) ? (labels[labels.length - 3] ?? second) : second;
}

/**
 * Validates a link against its platform. Returns an Italian error message, or null when valid.
 * Empty values pass: the required validator reports those.
 */
export function socialUrlError(platform: SocialPlatform, url: string): string | null {
  const value = (url ?? '').trim();
  if (!value) {
    return null;
  }

  if (platform === 'email') {
    return /^(mailto:)?[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(value) ? null : 'Inserisci un indirizzo email valido.';
  }

  // Any scheme other than http(s) (ftp:, javascript:, ...) is not a link we can render.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value) && !/^https?:\/\//i.test(value)) {
    return 'Inserisci un link valido.';
  }

  const brands = PLATFORM_BRANDS[platform];
  let host: string;
  try {
    host = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`).hostname.replace(/^www\./, '');
  } catch {
    return 'Inserisci un link valido.';
  }

  return brands && !brands.includes(brandOf(host)) ? `Il link deve contenere '${brands[0]}'` : null;
}
